import { spannungswandler } from '../../../../utils/apiTypes';
import { spannungswandlerState } from './spannungswandlerTypes';
import { AnyAction, createSlice, PayloadAction, ThunkAction } from "@reduxjs/toolkit";
import { getWithAuth, putWithAuth, deleteWithAuth } from "../../../../utils/api";
import type { RootState, AppDispatch } from "../../../../app/store";
import { logError } from "../../../../utils/utils";
import { openSnackbar } from "../../../../features/snackbar/globalSnackbarSlice";
import { GridSortModel } from '@material-ui/data-grid';
import { errorHandler } from '../../../../utils/errorHandling';

export const spannungswandlerDummy: spannungswandler
  = {
  faktor: "",
  kommentar: "",
  zugeordnet: false
};

const initialState: spannungswandlerState
  = {
  spannungswandlerListe: [],
  alteSpannungswandler: spannungswandlerDummy,
  aktuelleSpannungswandler: spannungswandlerDummy,
  newEntry: false,
  editEntry: false,
  editDirty: 0,
  viewMode: false,
  error: [],
  listLoading: false,
  sorting: [
    {
      field: 'faktor',
      sort: 'asc',
    },
  ]
};

const spannungswandlerSlice = createSlice({
  name: "spannungswandler",
  initialState,
  reducers:
  {
    updateSpannungswandlerR: (state, action: PayloadAction<spannungswandler>) => {
      const updateIndex = state.spannungswandlerListe.findIndex(
        (x: spannungswandler) => x.faktor == action.payload.faktor
      );
      const firstPart = state.spannungswandlerListe.slice(0, updateIndex);
      let lastPart = Array<spannungswandler>();
      if (updateIndex + 1 < state.spannungswandlerListe.length)
        lastPart = state.spannungswandlerListe.slice(updateIndex + 1);
      state.spannungswandlerListe = [...firstPart, action.payload, ...lastPart].sort((a, b) => a.faktor.localeCompare(b.faktor));
    },
    removeFromSpannungswandlerR: (state, action: PayloadAction<spannungswandler>) => {
      const updateIndex = state.spannungswandlerListe.findIndex(
        (x) => x.faktor == action.payload.faktor
      );
      const firstPart = state.spannungswandlerListe.slice(0, updateIndex);
      let lastPart = Array<spannungswandler>();
      if (updateIndex + 1 < state.spannungswandlerListe.length)
        lastPart = state.spannungswandlerListe.slice(updateIndex + 1);
      state.spannungswandlerListe = [...firstPart, ...lastPart];
    },
    addAktuellesSpannungswandlerToSpannungswandlerR: (state) => {
      const spannungswandlerArray = state.spannungswandlerListe;
      spannungswandlerArray.push(state.aktuelleSpannungswandler);
      state.spannungswandlerListe = [...spannungswandlerArray].sort((a, b) => a.faktor.localeCompare(b.faktor));
    },
    setSpannungswandlerR: (state, action: PayloadAction<Array<spannungswandler>>) => {
      state.spannungswandlerListe = action.payload;
    },
    setAktuellesSpannungswandler: (state, action: PayloadAction<spannungswandler>) => {
      state.aktuelleSpannungswandler = action.payload;
      state.alteSpannungswandler = action.payload;
    },
    setAltesSpannungswandler: (state, action: PayloadAction<spannungswandler>) => {
      state.alteSpannungswandler = action.payload;
    },
    resetAktuellesSpannungswandler: (state) => {
      state.aktuelleSpannungswandler = state.alteSpannungswandler;
    },
    setEditEntry: (state) => {
      state.editEntry = true;
    },
    resetEditEntry: (state) => {
      state.editEntry = false;
    },
    setNewEntry: (state) => {
      state.newEntry = true;
    },
    resetNewEntry: (state) => {
      state.newEntry = false;
    },
    resetEditDirty: (state) => {
      state.editDirty = 0;
    },
    setViewMode: (state) => {
      state.viewMode = true;
    },
    resetViewMode: (state) => {
      state.viewMode = false;
    },
    setError: (state, action: PayloadAction<string>) => {
      if (!state.error.includes(action.payload)) {
        state.error.push(action.payload);
      }
    },
    resetError: (state, action: PayloadAction<string>) => {
      if (state.error.includes(action.payload)) {
        state.error.splice(state.error.indexOf(action.payload), 1)
      }
    },
    setFaktor: (state, action: PayloadAction<string>) => {
      state.aktuelleSpannungswandler.faktor = action.payload;
      state.editDirty =
        state.aktuelleSpannungswandler.faktor ==
          state.alteSpannungswandler.faktor
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setKommentar: (state, action: PayloadAction<string>) => {
      state.aktuelleSpannungswandler.kommentar = action.payload;
      state.editDirty =
        state.aktuelleSpannungswandler.kommentar ==
          state.alteSpannungswandler.kommentar
          ? state.editDirty - 1
          : state.editDirty + 1;
    },
    setListLoading: (state, action: PayloadAction<boolean>) => {
      state.listLoading = action.payload;
    },
    setSorting: (state, action: PayloadAction<GridSortModel>) => {
      state.sorting = action.payload;
    },
  }
});


//Selectors
//======================================================================
export const selectSpannungswandlerListe = (state: RootState): Array<spannungswandler> =>
  state.spannungswandler.spannungswandlerListe;
export const selectAktuellesSpannungswandler = (state: RootState): spannungswandler =>
  state.spannungswandler.aktuelleSpannungswandler;
export const selectEditEntry = (state: RootState): boolean =>
  state.spannungswandler.editEntry;
export const selectNewEntry = (state: RootState): boolean => state.spannungswandler.newEntry;
export const selectEditDirty = (state: RootState): number =>
  state.spannungswandler.editDirty;
export const selectViewMode = (state: RootState): boolean => state.spannungswandler.viewMode;
export const selectError = (state: RootState): boolean => state.spannungswandler.error.length > 0;
export const selectListLoading = (state: RootState): boolean => state.spannungswandler.listLoading;
export const selectSorting = (state: RootState): GridSortModel => state.spannungswandler.sorting;

//Actions
//=====================================================================

export const getSpannungswandlerR = () => (dispatch: AppDispatch): void => {
  dispatch(setListLoading(true));
  getWithAuth("SPANNUNGSWANDLER", {})
    .then((response) => {
      dispatch(setSpannungswandlerR(response));
    })
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    }).finally(() => dispatch(setListLoading(false)));
};

export const updateSpannungswandler = (aktuelleSpannungswandler: spannungswandler): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  putWithAuth("SPANNUNGSWANDLERID", {}, aktuelleSpannungswandler, aktuelleSpannungswandler.faktor)
    .then(() => {
      dispatch(
        openSnackbar({
          message: `Spannungswandler mit Faktor ${aktuelleSpannungswandler.faktor} wurde erfolgreich geändert.`,
          severity: "success",
          duration: 6000,
        })
      );
      dispatch(setAltesSpannungswandler(aktuelleSpannungswandler));
      dispatch(updateSpannungswandlerR(aktuelleSpannungswandler));
      dispatch(resetEditEntry());
      dispatch(resetNewEntry());
      dispatch(resetEditDirty());
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    });

};

export const updateAktuellesSpannungswandler = (aktuelleSpannungswandler: spannungswandler): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  dispatch(setAktuellesSpannungswandler(aktuelleSpannungswandler));
  dispatch(setViewMode());
};

export const discardChanges = () => (dispatch: AppDispatch): void => {
  dispatch(resetAktuellesSpannungswandler());
  dispatch(resetEditEntry());
  dispatch(resetNewEntry());
  dispatch(resetEditDirty());
};

export const discardNew = () => (dispatch: AppDispatch): void => {
  dispatch(resetAktuellesSpannungswandler());
  dispatch(resetEditEntry());
  dispatch(resetNewEntry());
  dispatch(resetEditDirty());
  dispatch(resetViewMode());
};

export const startNewEntry = () => (dispatch: AppDispatch): void => {
  dispatch(setNewEntry());
  dispatch(setAktuellesSpannungswandler(spannungswandlerDummy));
  dispatch(setEditEntry());
  dispatch(setViewMode());
};

export const createSpannungswandler = (aktuelleSpannungswandler: spannungswandler): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  putWithAuth("SPANNUNGSWANDLERID", {}, aktuelleSpannungswandler, aktuelleSpannungswandler.faktor)
    .then(() => {
      dispatch(
        openSnackbar({
          message: `Spannungswandler mit Faktor ${aktuelleSpannungswandler.faktor} wurde erfolgreich angelegt.`,
          severity: "success",
          duration: 6000,
        })
      );
      dispatch(addAktuellesSpannungswandlerToSpannungswandlerR());
      dispatch(setAktuellesSpannungswandler(spannungswandlerDummy));
      dispatch(resetEditEntry());
      dispatch(resetNewEntry());
      dispatch(resetEditDirty());
      dispatch(resetViewMode());
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    });

};

export const deleteSpannungswandler = (aktuelleSpannungswandler: spannungswandler): ThunkAction<void, RootState, unknown, AnyAction> => (
  dispatch: AppDispatch
) => {
  deleteWithAuth("SPANNUNGSWANDLERID", { name: aktuelleSpannungswandler.faktor }, aktuelleSpannungswandler.faktor)
    .then(() => {
      dispatch(setAktuellesSpannungswandler(spannungswandlerDummy));
      dispatch(removeFromSpannungswandlerR(aktuelleSpannungswandler));
      dispatch(resetEditEntry());
      dispatch(resetEditDirty());
      dispatch(resetViewMode());
      dispatch(openSnackbar({
        message: `Spannungswandler mit Faktor ${aktuelleSpannungswandler.faktor} wurde erfolgreich gelöscht.`,
        severity: "success",
        duration: 6000,
      }));
    }
    )
    .catch((error) => {
      logError(error);
      errorHandler(error, dispatch);
    });
};

export const {
  removeFromSpannungswandlerR,
  setSpannungswandlerR,
  setAktuellesSpannungswandler,
  resetAktuellesSpannungswandler,
  setAltesSpannungswandler,
  updateSpannungswandlerR,
  setEditEntry,
  resetEditEntry,
  setNewEntry,
  resetNewEntry,
  resetEditDirty,
  setViewMode,
  resetViewMode,
  setError,
  resetError,
  setFaktor,
  setKommentar,
  addAktuellesSpannungswandlerToSpannungswandlerR,
  setListLoading,
  setSorting
} = spannungswandlerSlice.actions;
export default spannungswandlerSlice.reducer;